import { useEffect, useRef } from "react"
import { init, dispose, CandleType } from "klinecharts"
import type { Chart, KLineData } from "klinecharts"
import type { Candle, ChartMarker } from "../types/chart"
import { buildStyles, defaultBackground } from "../Workbench/chartStyles"
import { useIsDark } from "../Workbench/useIsDark"
import {
  registerBacktestChartOverlays,
  TRADE_MARKER,
} from "@/bot/components/backtestChartOverlays"
import { cn } from "@/lib/utils"

/**
 * Static candle preview: a fixed bar set with fixed markers, no data loading and
 * no interaction wiring. Used by the Backtest tab's placeholder while a backtest
 * is starting, so the tab shows the same engine and palette as the real chart
 * that replaces it.
 *
 * `theme` is a prop rather than read from context because the placeholder passes
 * it down explicitly; it falls back to the document's dark class.
 */

export interface ChartViewProps {
  ohlc: Candle[]
  markers?: ChartMarker[]
  theme?: "light" | "dark"
  /** Bar width in pixels. klinecharts' own default is 8. */
  barSpacing?: number
  className?: string
}

function toKline(c: Candle): KLineData {
  return {
    timestamp: c.time * 1000,
    open: c.open,
    high: c.high,
    low: c.low,
    close: c.close,
    volume: c.volume,
  }
}

/** Mute klinecharts' dev-only init banner (see BacktestChart.initQuietly). */
function initQuietly(el: HTMLElement): Chart | null {
  const nativeLog = console.log
  console.log = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].includes("Welcome to klinecharts")) return
    nativeLog(...args)
  }
  try {
    return init(el)
  } finally {
    console.log = nativeLog
  }
}

export function ChartView({ ohlc, markers = [], theme, barSpacing, className }: ChartViewProps) {
  const docIsDark = useIsDark()
  const isDark = theme != null ? theme === "dark" : docIsDark
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<Chart | null>(null)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    registerBacktestChartOverlays()
    const chart = initQuietly(el)
    chartRef.current = chart
    if (!chart) return undefined

    const ro = new ResizeObserver(() => chartRef.current?.resize())
    ro.observe(el)

    return () => {
      ro.disconnect()
      dispose(el)
      chartRef.current = null
    }
  }, [])

  useEffect(() => {
    chartRef.current?.setStyles(buildStyles(isDark, {}, CandleType.CandleSolid))
  }, [isDark])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.applyNewData(ohlc.map(toKline), false)
    if (barSpacing != null) chart.setBarSpace(barSpacing)

    // Markers are anchored to a bar's low/high, matching BacktestChart. The
    // preview data is static, so there's nothing to diff — applyNewData above
    // has already cleared the previous overlays.
    const byTime = new Map(ohlc.map((c) => [c.time, c]))
    for (const marker of markers) {
      const bar = byTime.get(marker.time)
      if (!bar) continue
      const up = marker.shape !== "arrowDown"
      chart.createOverlay({
        name: TRADE_MARKER,
        points: [{ timestamp: marker.time * 1000, value: up ? bar.low : bar.high }],
        extendData: { dir: up ? "up" : "down", color: marker.color, text: marker.text ?? "" },
      })
    }
  }, [ohlc, markers, barSpacing])

  return (
    <div className={cn("relative h-full w-full", className)}>
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ backgroundColor: defaultBackground(isDark) }}
      />
    </div>
  )
}
